import CloseIcon from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import {
  Box,
  Dialog as EditDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { updateBusinessName } from 'lib/api';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import BaknrruptcyPosts from 'pages/BusinessProfile/Others/components/Bankruptcy';
import LaborLawFinesTab from 'pages/BusinessProfile/Others/components/LaborLawFinesTab';
import RelatedBusinesses from 'pages/BusinessProfile/Others/components/Related';
import SocialSecurityArrearTab from 'pages/BusinessProfile/Others/components/SocialSecurityArrearTab';
import BankAccounts from 'pages/BusinessProfile/Profile/components/BankAccounts';
import Buro from 'pages/BusinessProfile/Profile/components/Buro';
import Client from 'pages/BusinessProfile/Profile/components/Client';
import Contacts from 'pages/BusinessProfile/Profile/components/Contacts';
import SuperDebt from 'pages/BusinessProfile/Profile/components/Debt';
import OperativeProfile from 'pages/BusinessProfile/Profile/components/Operative/components/OperativeProfile';
import StakeholderProfile from 'pages/BusinessProfile/Profile/components/StakeholderProfile';
import SupplierStats from 'pages/BusinessProfile/PublicMarket/components/TendersStats';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import {
  hideBusinessNetwork,
  setHasBuro,
  setHasSuperDebt,
  triggerRefresh,
} from 'services/redux/businessNetworkSlice';
import { RootState } from 'services/redux/store';

const GeneralCustomAlert = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const dispatch = useDispatch();
  const { open, business, hasBuro, hasSuperDebt } = useSelector(
    (state: RootState) => state.businessNetwork,
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const fetchIntegrations = async () => {
    const [superDebtIntegration, buroIntegration] = await Promise.all([
      getIntegrationStatusByInternalName(dispatch, 'super-debt'),
      getIntegrationStatusByInternalName(dispatch, 'buro'),
    ]);
    dispatch(setHasSuperDebt(superDebtIntegration?.status));
    dispatch(setHasBuro(buroIntegration?.status));
  };

  const handleClose = useCallback(() => {
    dispatch(hideBusinessNetwork());
  }, [dispatch]);

  const handleEditClick = () => {
    setNewBusinessName(business.name || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!business.id || !newBusinessName.trim()) return;

    setEditLoading(true);
    try {
      await updateBusinessName(dispatch, {
        identifier: business.id,
        name: newBusinessName.trim(),
      });

      showAlert(
        dispatch,
        'success',
        'Nombre de empresa actualizado correctamente',
      );
      setEditDialogOpen(false);
      dispatch(hideBusinessNetwork());
      dispatch(triggerRefresh());
      setTimeout(() => {
        dispatch(triggerRefresh());
      }, 100);
      window.dispatchEvent(new CustomEvent('businessNameUpdated'));
    } catch (error) {
      console.error('Error updating business name:', error);
      showAlert(
        dispatch,
        'error',
        'Error al actualizar el nombre de la empresa',
      );
    } finally {
      setEditLoading(false);
    }
  };

  const tabs = [
    {
      index: 0,
      label: 'Resumen',
      component: (
        <Client businessId={business.id} businessName={business.name} />
      ),
    },
    {
      index: 1,
      label: 'Relacionadas',
      component: (
        <RelatedBusinesses businessIdentifier={business.id as string} />
      ),
    },
    {
      index: 2,
      label: 'Contactos',
      component: <Contacts businessId={business.id} />,
    },
    {
      index: 3,
      label: 'Banco',
      component: <BankAccounts businessId={business.id} />,
    },
    {
      index: 4,
      label: 'Deuda',
      disabled: !hasSuperDebt,
      component: <SuperDebt businessId={business.id} />,
      hoverText: !hasSuperDebt
        ? 'No tienes activa la integración de deuda'
        : '',
    },
    {
      index: 5,
      label: 'Buró',
      disabled: !hasBuro,
      component: <Buro businessId={business.id} />,
      hoverText: !hasBuro ? 'No tienes activa la intración' : '',
    },
    {
      index: 6,
      label: 'Boletín Concursal',
      component: (
        <BaknrruptcyPosts businessIdentifier={business.id ?? undefined} />
      ),
    },
    {
      index: 6,
      label: 'Perfil Deudor',
      component: <StakeholderProfile businessId={business.id} />,
    },
    {
      index: 7,
      label: 'Mercado público',
      component: <SupplierStats id={business.id} />,
    },
    {
      index: 8,
      label: 'Perfil Operativo',
      component: <OperativeProfile businessIdentifier={business.id} />,
    },
    {
      index: 9,
      label: 'Mora Previsional',
      component: (
        <SocialSecurityArrearTab identifier={business.id ?? undefined} />
      ),
    },
    {
      index: 10,
      label: 'Multas Laborales',
      component: <LaborLawFinesTab identifier={business.id ?? undefined} />,
    },
  ];

  useEffect(() => {
    if (!user || !open) return;
    void fetchIntegrations();
  }, [business.id]);

  if (!open) return null;
  if (!user) return <div></div>;

  const canEditBusinessName = validatePermissions(
    user,
    'can_edit_business_name',
  );

  const editIconComponent = canEditBusinessName ? (
    <IconButton
      size="small"
      onClick={handleEditClick}
      sx={{
        padding: 1,
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.2)',
        },
      }}
    >
      <Edit sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
    </IconButton>
  ) : null;

  return (
    <>
      <Dialog maxWidth="xl" fullWidth open={open} onClose={handleClose}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h5"
                sx={{ mb: 0.5, fontWeight: 700, color: 'text.primary' }}
              >
                {business.name}
              </Typography>
              {editIconComponent}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.8rem' }}
            >
              {business.id}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <EnhancedTab
            startValue={0}
            tabs={tabs}
            editIcon={editIconComponent}
          />
        </Box>
      </Dialog>
      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Nombre de Empresa</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la empresa"
            type="text"
            fullWidth
            variant="outlined"
            value={newBusinessName}
            onChange={(e) => setNewBusinessName(e.target.value)}
            disabled={editLoading}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setEditDialogOpen(false)}
            disabled={editLoading}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={handleSaveEdit}
            disabled={editLoading || !newBusinessName.trim()}
            color="primary"
          >
            {editLoading ? 'Guardando...' : 'Guardar'}
          </CustomButton>
        </DialogActions>
      </EditDialog>
    </>
  );
};

export default GeneralCustomAlert;
